// The demo seat a plan was started from, resolved to the two things a screen actually
// wants to say to the person in it: their first name, and the scenario's own one-line
// introduction for them ("Daniel, the father"). Neither `readDemoSource` nor
// `listDemoScenarios` alone carries this — the source names a scenario and a seat; the
// catalog is where that seat's `name` and `introduction` actually live — so this hook
// makes both reads and matches them itself, for whichever screen needs the result.
// `PlanScreen.tsx`'s header link and `DemoWelcome.tsx`'s arrival dialog are the two
// callers, and each takes its own read from this hook rather than one being threaded
// state from the other.
//
// Resolves to `null` for every shape of "nothing to show": not a demo, the source read
// failed, or the scenario or seat it names is no longer in the catalog. Every caller
// already has a plain fallback for that — the header keeps its generic label, the
// welcome simply never appears — so this hook reports nothing more specific than "there
// is no seat to introduce" and lets the caller decide what that means to it.

import { useEffect, useState } from 'react';

import type { ApiClient } from '../api/client.js';
import type { DemoScenario } from '../api/contract.js';

export type DemoSeatIntro = {
  /** The person's first name, as the scenario writes it — "Daniel", never "daniel-weiss". */
  readonly firstName: string;
  /** The scenario's own one-line introduction for this seat, e.g. "Daniel, the father". */
  readonly introduction: string;
  /** This seat, named so it reads on its own — e.g. "Maya, Daniel's wife". What
   *  `welcome-text.ts` substitutes `{{character}}` with, falling back to `introduction`
   *  when this is empty. See `@mor/eval`'s `scenario.ts` for
   *  `ScenarioPerson.standaloneIntroduction`, the field this carries. */
  readonly standaloneIntroduction: string;
  /** The scenario's own welcome, as paragraphs — `DemoScenario.welcome` — the first shown
   *  as the dialog's heading, the rest as body prose. May carry `{{character}}` in place of
   *  this seat's own introduction; `welcome-text.ts` resolves it. Empty when the author has
   *  not written one. */
  readonly welcome: readonly string[];
};

/**
 * The first word of whichever of `introduction` (preferred) or `name` actually has
 * something in it, split on the first comma first — "Daniel, the father" and "Daniel
 * Weiss" both give "Daniel". A seat with neither written yet has nothing to introduce,
 * which the caller matching this into `null` already covers.
 */
function firstNameOf(seat: { name: string; introduction: string }): string {
  const source = seat.introduction.trim().length > 0 ? seat.introduction : seat.name;
  const beforeComma = source.split(',')[0] ?? source;
  const firstWord = beforeComma.trim().split(/\s+/u)[0];
  return firstWord ?? beforeComma.trim();
}

function seatIn(
  scenarios: readonly DemoScenario[],
  scenarioId: string,
  seatLabel: string,
): DemoSeatIntro | null {
  const scenario = scenarios.find((candidate) => candidate.id === scenarioId);
  const seat = scenario?.seats.find((candidate) => candidate.label === seatLabel);
  if (seat === undefined || scenario === undefined) {
    return null;
  }
  return {
    firstName: firstNameOf(seat),
    introduction: seat.introduction,
    standaloneIntroduction: seat.standaloneIntroduction,
    welcome: scenario.welcome,
  };
}

/** `null` until resolved, and while there is nothing to resolve to — see the header comment. */
export function useDemoSeatIntro(
  client: ApiClient,
  planId: string,
  isDemo: boolean,
): DemoSeatIntro | null {
  const [seat, setSeat] = useState<DemoSeatIntro | null>(null);

  useEffect(() => {
    if (!isDemo || planId === '') {
      return;
    }
    let live = true;
    client
      .readDemoSource(planId)
      .then((source) =>
        client.listDemoScenarios().then((scenarios) => {
          if (live) {
            setSeat(seatIn(scenarios, source.scenarioId, source.seatLabel));
          }
        }),
      )
      .catch(() => {
        // Nothing recorded for this plan, or the catalog read failed — either way there
        // is nothing to introduce, and every caller already has a fallback that needs no
        // error state of its own.
      });
    return () => {
      live = false;
    };
  }, [client, planId, isDemo]);

  return seat;
}
